import { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, XCircle, AlertTriangle, Loader2, FileText } from 'lucide-react';
import { api } from '../../api/client';

type VatRecord = {
  id: string;
  recordType: 'individual' | 'group';
  externalId: number | null;
  status: string;
  year: number;
  vatPeriod: number;
  documentNumber: string | null;
  documentType: number | null;
  documentDirection: number | null;
  baseAmount20: string;
  vat20: string;
  baseAmount10: string;
  vat10: string;
  totalVat: string;
  relatedPartyId: string | null;
  createdAt: string;
};

const statusColors: Record<string, { bg: string; color: string }> = {
  Draft: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  Recorded: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80' },
  Replaced: { bg: 'rgba(234,179,8,0.2)', color: '#facc15' },
  Cancelled: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5' },
};

const documentTypes = [
  { value: 380, label: '380 - Faktura' },
  { value: 381, label: '381 - Odobrenje' },
  { value: 383, label: '383 - Zaduženje' },
  { value: 386, label: '386 - Avansna' },
  { value: 400, label: '400 - Fiskalni račun' },
  { value: 401, label: '401 - Ostali dokument' },
];

const monthsOrQuarters = [
  { value: 1, label: 'Januar' },
  { value: 2, label: 'Februar' },
  { value: 3, label: 'Mart' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Avgust' },
  { value: 9, label: 'Septembar' },
  { value: 10, label: 'Oktobar' },
  { value: 11, label: 'Novembar' },
  { value: 12, label: 'Decembar' },
  { value: 13, label: 'Q1' },
  { value: 14, label: 'Q2' },
  { value: 15, label: 'Q3' },
  { value: 16, label: 'Q4' },
];

export function VatRecordingPage({ tenantId }: { tenantId: string }) {
  const [records, setRecords] = useState<VatRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Filters
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth() + 1;
  const [filterYear, setFilterYear] = useState(thisYear);
  const [filterPeriod, setFilterPeriod] = useState<number | 'all'>('all');

  // Form
  const [formYear, setFormYear] = useState(thisYear);
  const [formPeriod, setFormPeriod] = useState(thisMonth);
  const [formDocNumber, setFormDocNumber] = useState('');
  const [formDocType, setFormDocType] = useState(380);
  const [formDirection, setFormDirection] = useState(1);   // 1=outbound
  const [formBase20, setFormBase20] = useState('0');
  const [formVat20, setFormVat20] = useState('0');
  const [formBase10, setFormBase10] = useState('0');
  const [formVat10, setFormVat10] = useState('0');
  const [formRelatedParty, setFormRelatedParty] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRecords = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId, year: String(filterYear) });
      if (filterPeriod !== 'all') params.set('vatPeriod', String(filterPeriod));
      const data = await api.get<VatRecord[]>(`/admin/efaktura-vat/kev?${params}`);
      setRecords(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [tenantId, filterYear, filterPeriod]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.post('/admin/efaktura-vat/kev/individual', {
        tenantId,
        year: formYear,
        vatPeriod: formPeriod,
        documentNumber: formDocNumber,
        documentType: formDocType,
        documentDirection: formDirection,
        baseAmount20: Number(formBase20) || 0,
        vat20: Number(formVat20) || 0,
        baseAmount10: Number(formBase10) || 0,
        vat10: Number(formVat10) || 0,
        relatedPartyId: formRelatedParty || undefined,
      });
      setNotice('KEV zapis kreiran.');
      setShowForm(false);
      setFormDocNumber('');
      setFormBase20('0'); setFormVat20('0');
      setFormBase10('0'); setFormVat10('0');
      setFormRelatedParty('');
      await fetchRecords();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri kreiranju');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Poništiti KEV zapis na portalu?')) return;
    setActionBusy(id);
    setError(null);
    try {
      await api.post(`/admin/efaktura-vat/kev/${id}/cancel`, {});
      await fetchRecords();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri poništavanju');
    } finally {
      setActionBusy(null);
    }
  };

  const handleRefresh = async (id: string) => {
    setActionBusy(id);
    setError(null);
    try {
      await api.post(`/admin/efaktura-vat/kev/${id}/refresh`, {});
      await fetchRecords();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setActionBusy(null);
    }
  };

  const fmt = (n: string | number) =>
    new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));

  const periodLabel = (p: number) => monthsOrQuarters.find((m) => m.value === p)?.label ?? String(p);
  const directionLabel = (d: number | null) => d === 0 ? 'Ulazni' : d === 1 ? 'Izlazni' : '-';

  // Aggregates for the currently filtered set
  const totals = records.reduce((acc, r) => ({
    base20: acc.base20 + Number(r.baseAmount20),
    vat20: acc.vat20 + Number(r.vat20),
    base10: acc.base10 + Number(r.baseAmount10),
    vat10: acc.vat10 + Number(r.vat10),
    totalVat: acc.totalVat + Number(r.totalVat),
  }), { base20: 0, vat20: 0, base10: 0, vat10: 0, totalVat: 0 });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Godina:</label>
        <input type="number" value={filterYear} onChange={(e) => setFilterYear(parseInt(e.target.value) || thisYear)} style={{ ...inputStyle, width: 100 }} />

        <label style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Period:</label>
        <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value === 'all' ? 'all' : parseInt(e.target.value))} style={selectStyle}>
          <option value="all">Svi</option>
          {monthsOrQuarters.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>

        <div style={{ flex: 1 }} />
        <button onClick={() => setShowForm((v) => !v)} disabled={!tenantId} style={btnPrimary}>
          <Plus size={14} /> {showForm ? 'Sakrij formu' : 'Novi zapis'}
        </button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      {showForm && (
        <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
            Nov pojedinačan KEV zapis
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr', gap: 10 }}>
            <FormField label="Godina">
              <input type="number" value={formYear} onChange={(e) => setFormYear(parseInt(e.target.value) || thisYear)} style={inputStyle} />
            </FormField>
            <FormField label="Period">
              <select value={formPeriod} onChange={(e) => setFormPeriod(parseInt(e.target.value))} style={selectStyle}>
                {monthsOrQuarters.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </FormField>
            <FormField label="Tip dokumenta">
              <select value={formDocType} onChange={(e) => setFormDocType(parseInt(e.target.value))} style={selectStyle}>
                {documentTypes.map((dt) => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
              </select>
            </FormField>
            <FormField label="Smer">
              <select value={formDirection} onChange={(e) => setFormDirection(parseInt(e.target.value))} style={selectStyle}>
                <option value={1}>Izlazni</option>
                <option value={0}>Ulazni</option>
              </select>
            </FormField>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            <FormField label="Broj dokumenta">
              <input type="text" value={formDocNumber} onChange={(e) => setFormDocNumber(e.target.value)} style={inputStyle} />
            </FormField>
            <FormField label="PIB povezane strane">
              <input type="text" value={formRelatedParty} onChange={(e) => setFormRelatedParty(e.target.value)} style={inputStyle} placeholder="9 cifara" />
            </FormField>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 10 }}>
            <FormField label="Osnovica 20%">
              <input type="number" step="any" value={formBase20} onChange={(e) => setFormBase20(e.target.value)} style={inputStyle} />
            </FormField>
            <FormField label="PDV 20%">
              <input type="number" step="any" value={formVat20} onChange={(e) => setFormVat20(e.target.value)} style={inputStyle} />
            </FormField>
            <FormField label="Osnovica 10%">
              <input type="number" step="any" value={formBase10} onChange={(e) => setFormBase10(e.target.value)} style={inputStyle} />
            </FormField>
            <FormField label="PDV 10%">
              <input type="number" step="any" value={formVat10} onChange={(e) => setFormVat10(e.target.value)} style={inputStyle} />
            </FormField>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button onClick={handleCreate} disabled={saving} style={btnPrimary}>
              {saving ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
              {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>God</th>
                <th style={th}>Period</th>
                <th style={th}>Dok.</th>
                <th style={th}>Tip</th>
                <th style={th}>Smer</th>
                <th style={{ ...th, textAlign: 'right' }}>Osn. 20</th>
                <th style={{ ...th, textAlign: 'right' }}>PDV 20</th>
                <th style={{ ...th, textAlign: 'right' }}>Osn. 10</th>
                <th style={{ ...th, textAlign: 'right' }}>PDV 10</th>
                <th style={{ ...th, textAlign: 'right' }}>Uk. PDV</th>
                <th style={th}>Status</th>
                <th style={{ ...th, width: 100 }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && records.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                    <FileText size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                    <div>Nema KEV zapisa za izabrani period.</div>
                  </td>
                </tr>
              )}
              {records.map((r) => {
                const sc = statusColors[r.status] || statusColors.Draft;
                return (
                  <tr key={r.id}>
                    <td style={td}>{r.year}</td>
                    <td style={td}>{periodLabel(r.vatPeriod)}</td>
                    <td style={{ ...td, color: 'var(--color-text-strong)' }}>{r.documentNumber || '-'}</td>
                    <td style={td}>{r.documentType ?? '-'}</td>
                    <td style={td}>{directionLabel(r.documentDirection)}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.baseAmount20)}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.vat20)}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.baseAmount10)}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.vat10)}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--color-text-strong)' }}>{fmt(r.totalVat)}</td>
                    <td style={td}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: sc.bg, color: sc.color, fontWeight: 700 }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => handleRefresh(r.id)} disabled={actionBusy === r.id || !r.externalId} style={iconBtn} title="Osveži">
                          {actionBusy === r.id ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
                        </button>
                        {r.status !== 'Cancelled' && r.externalId && (
                          <button onClick={() => handleCancel(r.id)} disabled={actionBusy === r.id} style={{ ...iconBtn, color: '#fca5a5' }} title="Poništi">
                            <XCircle size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {records.length > 0 && (
              <tfoot>
                <tr style={{ background: 'var(--color-bg)' }}>
                  <td colSpan={5} style={{ ...td, fontWeight: 700, color: 'var(--color-text-strong)' }}>Ukupno ({records.length})</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(totals.base20)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(totals.vat20)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(totals.base10)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(totals.vat10)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-strong)' }}>{fmt(totals.totalVat)}</td>
                  <td colSpan={2} style={td} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

const th: React.CSSProperties = { padding: '10px 12px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const inputStyle: React.CSSProperties = { padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', width: '100%' };
const selectStyle: React.CSSProperties = { padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', width: '100%' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 4, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const noticeBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 16 };
